import { BlockId } from '../world/BlockType.js';
import { BLOCK_COLORS, BLOCK_TOP_COLORS, BLOCK_NAMES, ITEM_COLORS_MAP, ITEM_NAMES_MAP, renderBlockIcon, renderItemIcon } from './Hotbar.js';
import { ItemId } from '../items/ItemRegistry.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;

// All placeable blocks for creative palette (exclude AIR, WATER, BEDROCK, DOOR_OPEN)
const CREATIVE_BLOCKS = [
  BlockId.GRASS, BlockId.DIRT, BlockId.STONE, BlockId.SAND,
  BlockId.WOOD, BlockId.LEAVES, BlockId.SNOW_GRASS, BlockId.SANDSTONE,
  BlockId.CACTUS, BlockId.PLANKS, BlockId.COBBLESTONE, BlockId.GLASS,
  BlockId.WHEAT, BlockId.DOOR_CLOSED, BlockId.COAL_ORE, BlockId.IRON_ORE,
  BlockId.GOLD_ORE, BlockId.DIAMOND_ORE, BlockId.REDSTONE_ORE, BlockId.LAPIS_ORE,
  BlockId.EMERALD_ORE, BlockId.OBSIDIAN, BlockId.TORCH, BlockId.CRAFTING_TABLE,
  BlockId.FURNACE, BlockId.CHEST, BlockId.TNT, BlockId.BOOKSHELF,
  BlockId.BRICKS, BlockId.IRON_BLOCK, BlockId.GOLD_BLOCK, BlockId.DIAMOND_BLOCK,
  BlockId.SLAB_STONE, BlockId.SLAB_WOOD, BlockId.FENCE, BlockId.LADDER,
  BlockId.FLOWER_RED, BlockId.FLOWER_YELLOW, BlockId.GRAVEL, BlockId.CLAY,
  BlockId.LOG_BIRCH, BlockId.LEAVES_BIRCH, BlockId.SNOW, BlockId.ICE,
  BlockId.PUMPKIN, BlockId.MELON, BlockId.WOOL_WHITE, BlockId.MOSS_STONE,
  BlockId.NETHERRACK, BlockId.GLOWSTONE, BlockId.SOUL_SAND, BlockId.NETHER_BRICKS,
  BlockId.END_STONE, BlockId.BED,
  BlockId.FARMLAND, BlockId.FARMLAND_WET,
  BlockId.JUNGLE_LOG, BlockId.JUNGLE_LEAVES, BlockId.ACACIA_LOG, BlockId.ACACIA_LEAVES,
  BlockId.VINE, BlockId.LILY_PAD,
  BlockId.ENCHANTING_TABLE,
  BlockId.BREWING_STAND,
  BlockId.LAVA,
  BlockId.NETHER_GOLD_ORE,
  BlockId.NETHER_QUARTZ_ORE,
  BlockId.REDSTONE_WIRE,
  BlockId.LEVER,
  BlockId.BUTTON,
  BlockId.PRESSURE_PLATE,
  BlockId.REDSTONE_TORCH,
  BlockId.REPEATER,
  BlockId.TRAPDOOR,
  BlockId.STAIRS_STONE,
  BlockId.STAIRS_WOOD,
  BlockId.SIGN,
  BlockId.JUKEBOX,
  BlockId.NOTEBLOCK,
  BlockId.COFFIN,
  BlockId.CROSS,
  BlockId.GRAVE_BASE,
  BlockId.END_PORTAL_FRAME,
  BlockId.PURPUR_BLOCK,
  BlockId.PURPUR_PILLAR,
  BlockId.END_STONE_BRICKS,
  BlockId.CHORUS_PLANT,
  BlockId.DRAGON_EGG,
  BlockId.END_ROD,
  // Farming & Village blocks (Fase 5)
  BlockId.CARROT,
  BlockId.POTATO,
  BlockId.BEETROOT,
  BlockId.BERRY_BUSH,
  BlockId.PUMPKIN_STEM,
  BlockId.MELON_STEM,
  BlockId.SUGAR_CANE,
  BlockId.BELL,
  // Redstone automation (Fase 6)
  BlockId.PISTON,
  BlockId.STICKY_PISTON,
  BlockId.HOPPER,
  BlockId.DROPPER,
  BlockId.DISPENSER,
  BlockId.OBSERVER,
  BlockId.REDSTONE_LAMP,
  BlockId.DAYLIGHT_SENSOR,
  BlockId.POWERED_RAIL,
  BlockId.DETECTOR_RAIL,
  BlockId.RAIL,
  // Exploration blocks (Fase 7)
  BlockId.DRIPSTONE,
  BlockId.POINTED_DRIPSTONE,
  BlockId.AZALEA,
  BlockId.AZALEA_LEAVES,
  BlockId.GLOW_BERRY_VINE,
  BlockId.SCULK,
  BlockId.SCULK_SENSOR,
  BlockId.MOSS_BLOCK,
  BlockId.MYCELIUM,
  BlockId.TERRACOTTA,
  BlockId.TERRACOTTA_RED,
  BlockId.TERRACOTTA_ORANGE,
  // Decorative blocks (Fase 8)
  BlockId.WOOL_RED,
  BlockId.WOOL_BLUE,
  BlockId.WOOL_GREEN,
  BlockId.WOOL_YELLOW,
  BlockId.WOOL_BLACK,
  BlockId.WOOL_ORANGE,
  BlockId.WOOL_PURPLE,
  BlockId.GLASS_RED,
  BlockId.GLASS_BLUE,
  BlockId.GLASS_GREEN,
  BlockId.GLASS_YELLOW,
  BlockId.LANTERN,
  BlockId.CHAIN,
  BlockId.CAMPFIRE,
  BlockId.CAMPFIRE_LIT,
  BlockId.CANDLE,
  BlockId.SCAFFOLDING,
  BlockId.BARREL,
  BlockId.LOOM,
  BlockId.SMOKER,
];

// Non-block items available in creative palette
const CREATIVE_ITEMS = [
  ItemId.STICK, ItemId.COAL, ItemId.RAW_IRON, ItemId.RAW_GOLD,
  ItemId.DIAMOND_GEM, ItemId.EMERALD_GEM, ItemId.REDSTONE_DUST, ItemId.LAPIS,
  ItemId.IRON_INGOT, ItemId.GOLD_INGOT,
  ItemId.FLINT, ItemId.STRING, ItemId.FEATHER, ItemId.LEATHER, ItemId.BONE,
  ItemId.BOW, ItemId.ARROW_ITEM,
  ItemId.RAW_BEEF, ItemId.COOKED_BEEF, ItemId.RAW_PORK, ItemId.COOKED_PORK,
  ItemId.RAW_CHICKEN, ItemId.COOKED_CHICKEN, ItemId.BREAD, ItemId.APPLE,
  ItemId.WHEAT_ITEM, ItemId.SEEDS, ItemId.BONE_MEAL,
  ItemId.GUNPOWDER, ItemId.ROTTEN_FLESH, ItemId.SPIDER_EYE,
  ItemId.SLIME_BALL, ItemId.ENDER_PEARL,
  ItemId.GLASS_BOTTLE, ItemId.NETHER_WART, ItemId.BLAZE_POWDER,
  ItemId.AWKWARD_POTION,
  ItemId.POTION_SPEED, ItemId.POTION_STRENGTH, ItemId.POTION_HEALING,
  ItemId.POTION_FIRE_RESISTANCE, ItemId.POTION_NIGHT_VISION, ItemId.POTION_REGENERATION,
  ItemId.GHAST_TEAR, ItemId.BLAZE_ROD, ItemId.NETHER_QUARTZ,
  ItemId.IRON_HELMET, ItemId.IRON_CHESTPLATE, ItemId.IRON_LEGGINGS, ItemId.IRON_BOOTS,
  ItemId.BUCKET, ItemId.PAINTING, ItemId.BOOK, ItemId.MINECART, ItemId.RAILS, ItemId.PISTON,
  ItemId.GLOWSTONE_DUST, ItemId.SNOWBALL, ItemId.BRICK_ITEM,
  // Fishing
  ItemId.FISHING_ROD,
  ItemId.RAW_COD, ItemId.COOKED_COD, ItemId.RAW_SALMON, ItemId.COOKED_SALMON,
  ItemId.TROPICAL_FISH, ItemId.PUFFERFISH,
  // Boss items
  ItemId.NETHER_STAR, ItemId.WITHER_SKULL,
  // Farming & Village items (Fase 5)
  ItemId.CARROT_ITEM, ItemId.POTATO_ITEM, ItemId.BEETROOT_ITEM,
  ItemId.BAKED_POTATO, ItemId.BEETROOT_SOUP, ItemId.SWEET_BERRIES,
  ItemId.SUGAR, ItemId.PUMPKIN_SEEDS, ItemId.MELON_SEEDS, ItemId.MELON_SLICE,
  ItemId.TRIDENT,
  // Exploration & Adventure items (Fase 7)
  ItemId.SHIELD, ItemId.CROSSBOW, ItemId.HEART_OF_SEA, ItemId.TREASURE_MAP,
  // Tecnología (Fase 17)
  ItemId.PHONE, ItemId.COMPUTER,
];

export class InventoryScreen {
  constructor(inventory) {
    this.inventory = inventory;
    this.isOpen = false;
    this.heldItem = null;
    this.heldFrom = null;
    this.searchText = '';
    this._soundManager = null;

    // --- Build DOM (Minecraft inventory style) ---
    this.overlay = document.createElement('div');
    this.overlay.id = 'inventory-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55);
      z-index: 500;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
    `;

    // Main panel (Minecraft-style gray background with dark border)
    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF;
      border-left-color: #FFFFFF;
      padding: 8px;
      font-family: 'Courier New', monospace;
      color: #404040;
      user-select: none;
      image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
      max-height: 90vh;
      overflow-y: auto;
    `;

    // === CREATIVE PALETTE SECTION ===
    const creativeTitle = document.createElement('div');
    creativeTitle.textContent = 'Todos los Bloques';
    creativeTitle.style.cssText = `
      font-size: 14px;
      margin-bottom: 4px;
      color: #404040;
      text-shadow: 1px 1px 0 rgba(255,255,255,0.4);
      font-weight: bold;
    `;
    this.panel.appendChild(creativeTitle);

    // Search bar
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Buscar bloques...';
    this.searchInput.style.cssText = `
      width: 100%;
      padding: 4px 8px;
      margin-bottom: 6px;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      background: #8B8B8B;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      color: #FFFFFF;
      outline: none;
      box-sizing: border-box;
      text-shadow: 1px 1px 0 #3F3F3F;
    `;
    this.searchInput.addEventListener('input', () => {
      this.searchText = this.searchInput.value.toLowerCase();
      this._renderCreativePalette();
    });
    this.panel.appendChild(this.searchInput);

    // Creative blocks grid
    this.creativeGrid = document.createElement('div');
    this.creativeGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 8px;
    `;
    this.creativeSlots = [];
    this.panel.appendChild(this.creativeGrid);

    // Build creative slots
    this._buildCreativeSlots();

    // Separator
    const sepCreative = document.createElement('div');
    sepCreative.style.cssText = `
      height: 2px;
      background: linear-gradient(to right, transparent, #5A5A5A 10%, #5A5A5A 90%, transparent);
      margin: 6px 0;
    `;
    this.panel.appendChild(sepCreative);

    // === PLAYER INVENTORY SECTION ===
    const title = document.createElement('div');
    title.textContent = 'Inventario';
    title.style.cssText = `
      font-size: 14px;
      margin-bottom: 6px;
      color: #404040;
      text-shadow: 1px 1px 0 rgba(255,255,255,0.4);
      font-weight: bold;
    `;
    this.panel.appendChild(title);

    // Storage grid (3 rows x 9 cols)
    this.storageGrid = document.createElement('div');
    this.storageGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 8px;
    `;
    this.storageSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot('storage', i);
      this.storageSlots.push(slot);
      this.storageGrid.appendChild(slot);
    }
    this.panel.appendChild(this.storageGrid);

    // Separator line
    const sep = document.createElement('div');
    sep.style.cssText = `
      height: 2px;
      background: linear-gradient(to right, transparent, #8B8B8B 10%, #8B8B8B 90%, transparent);
      margin: 6px 0;
    `;
    this.panel.appendChild(sep);

    // Hotbar row (1 row x 9 cols)
    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlotEls = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot('hotbar', i);
      this.hotbarSlotEls.push(slot);
      this.hotbarGrid.appendChild(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    this.overlay.appendChild(this.panel);

    // Held item cursor element
    this.cursorItem = document.createElement('div');
    this.cursorItem.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 600;
      display: none;
    `;
    document.body.appendChild(this.cursorItem);

    // Tooltip (Minecraft style - dark purple/black bg)
    this.tooltip = document.createElement('div');
    this.tooltip.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 600;
      background: #100010;
      color: white;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      padding: 4px 8px;
      border: 2px solid #5000A0;
      display: none;
      text-shadow: 1px 1px 0 #3F3F3F;
      white-space: nowrap;
    `;
    document.body.appendChild(this.tooltip);

    document.body.appendChild(this.overlay);

    // Track mouse for held item
    this._onMouseMove = (e) => {
      if (!this.isOpen) return;
      this.cursorItem.style.left = (e.clientX + 10) + 'px';
      this.cursorItem.style.top = (e.clientY + 10) + 'px';
      this.tooltip.style.left = (e.clientX + 15) + 'px';
      this.tooltip.style.top = (e.clientY - 30) + 'px';
    };
    // mousemove listener added in open(), removed in close()
  }

  _buildCreativeSlots() {
    this.creativeGrid.innerHTML = '';
    this.creativeSlots = [];

    // Filter blocks
    const filteredBlocks = CREATIVE_BLOCKS.filter(id => {
      if (!this.searchText) return true;
      const name = (BLOCK_NAMES[id] || '').toLowerCase();
      return name.includes(this.searchText);
    });

    // Filter items
    const filteredItems = CREATIVE_ITEMS.filter(id => {
      if (!this.searchText) return true;
      const name = (ITEM_NAMES_MAP[id] || '').toLowerCase();
      return name.includes(this.searchText);
    });

    // Render block slots
    for (const blockId of filteredBlocks) {
      const slot = document.createElement('div');
      slot.style.cssText = `
        width: ${SLOT_SIZE}px;
        height: ${SLOT_SIZE}px;
        background: #8B8B8B;
        border: 2px solid;
        border-color: #373737 #FFFFFF #FFFFFF #373737;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        cursor: pointer;
      `;
      slot.innerHTML = renderBlockIcon(blockId, 28);

      slot.addEventListener('mouseenter', () => {
        slot.style.background = '#A0A0A0';
        const name = BLOCK_NAMES[blockId] || '';
        this.tooltip.textContent = name;
        this.tooltip.style.display = 'block';
      });
      slot.addEventListener('mouseleave', () => {
        slot.style.background = '#8B8B8B';
        this.tooltip.style.display = 'none';
      });
      slot.addEventListener('click', (e) => {
        if (e.shiftKey) {
          this._onCreativeShiftClick(blockId, 'block');
        } else {
          this._onCreativeClick(blockId);
        }
      });

      this.creativeSlots.push(slot);
      this.creativeGrid.appendChild(slot);
    }

    // Render item slots
    for (const itemId of filteredItems) {
      const slot = document.createElement('div');
      slot.style.cssText = `
        width: ${SLOT_SIZE}px;
        height: ${SLOT_SIZE}px;
        background: #8B8B8B;
        border: 2px solid;
        border-color: #373737 #FFFFFF #FFFFFF #373737;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        cursor: pointer;
      `;
      slot.innerHTML = renderItemIcon(itemId, 28);

      slot.addEventListener('mouseenter', () => {
        slot.style.background = '#A0A0A0';
        const name = ITEM_NAMES_MAP[itemId] || 'Objeto';
        this.tooltip.textContent = name;
        this.tooltip.style.display = 'block';
      });
      slot.addEventListener('mouseleave', () => {
        slot.style.background = '#8B8B8B';
        this.tooltip.style.display = 'none';
      });
      slot.addEventListener('click', (e) => {
        if (e.shiftKey) {
          this._onCreativeShiftClick(itemId, 'item');
        } else {
          this._onCreativeItemClick(itemId);
        }
      });

      this.creativeSlots.push(slot);
      this.creativeGrid.appendChild(slot);
    }

    // Fill remaining slots in last row to keep grid aligned
    const totalItems = filteredBlocks.length + filteredItems.length;
    const remainder = totalItems % 9;
    if (remainder > 0) {
      for (let i = 0; i < 9 - remainder; i++) {
        const empty = document.createElement('div');
        empty.style.cssText = `width:${SLOT_SIZE}px;height:${SLOT_SIZE}px;`;
        this.creativeGrid.appendChild(empty);
      }
    }
  }

  _renderCreativePalette() {
    this._buildCreativeSlots();
  }

  _onCreativeClick(blockId) {
    // Give player a full stack of MAX_STACK_SIZE of this block
    // First try to add to existing stack, then empty slot
    let added = false;
    // Check hotbar
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const s = this.inventory.slots[i];
      if (s && s.blockId === blockId && s.count < MAX_STACK_SIZE) {
        s.count = MAX_STACK_SIZE;
        added = true;
        break;
      }
    }
    if (!added) {
      // Check storage
      for (let i = 0; i < this.inventory.storage.length; i++) {
        const s = this.inventory.storage[i];
        if (s && s.blockId === blockId && s.count < MAX_STACK_SIZE) {
          s.count = MAX_STACK_SIZE;
          added = true;
          break;
        }
      }
    }
    if (!added) {
      // Find empty hotbar slot
      for (let i = 0; i < this.inventory.slots.length; i++) {
        if (!this.inventory.slots[i]) {
          this.inventory.slots[i] = { blockId, count: MAX_STACK_SIZE };
          added = true;
          break;
        }
      }
    }
    if (!added) {
      // Find empty storage slot
      for (let i = 0; i < this.inventory.storage.length; i++) {
        if (!this.inventory.storage[i]) {
          this.inventory.storage[i] = { blockId, count: MAX_STACK_SIZE };
          added = true;
          break;
        }
      }
    }
    this._render();
  }

  _onCreativeItemClick(itemId) {
    // Give player a full stack of MAX_STACK_SIZE of this item
    let added = false;
    // Check hotbar for existing stack
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const s = this.inventory.slots[i];
      if (s && s.itemId === itemId && s.count < MAX_STACK_SIZE) {
        s.count = MAX_STACK_SIZE;
        added = true;
        break;
      }
    }
    if (!added) {
      // Check storage
      for (let i = 0; i < this.inventory.storage.length; i++) {
        const s = this.inventory.storage[i];
        if (s && s.itemId === itemId && s.count < MAX_STACK_SIZE) {
          s.count = MAX_STACK_SIZE;
          added = true;
          break;
        }
      }
    }
    if (!added) {
      // Find empty hotbar slot
      for (let i = 0; i < this.inventory.slots.length; i++) {
        if (!this.inventory.slots[i]) {
          this.inventory.slots[i] = { itemId, count: MAX_STACK_SIZE };
          added = true;
          break;
        }
      }
    }
    if (!added) {
      // Find empty storage slot
      for (let i = 0; i < this.inventory.storage.length; i++) {
        if (!this.inventory.storage[i]) {
          this.inventory.storage[i] = { itemId, count: MAX_STACK_SIZE };
          added = true;
          break;
        }
      }
    }
    this._render();
  }

  /**
   * Shift-click on creative palette: add full stack to first empty hotbar slot.
   */
  _onCreativeShiftClick(id, type) {
    for (let i = 0; i < this.inventory.slots.length; i++) {
      if (!this.inventory.slots[i]) {
        if (type === 'block') {
          this.inventory.slots[i] = { blockId: id, count: MAX_STACK_SIZE };
        } else {
          this.inventory.slots[i] = { itemId: id, count: MAX_STACK_SIZE };
        }
        this._render();
        return;
      }
    }
    // If hotbar is full, try storage
    for (let i = 0; i < this.inventory.storage.length; i++) {
      if (!this.inventory.storage[i]) {
        if (type === 'block') {
          this.inventory.storage[i] = { blockId: id, count: MAX_STACK_SIZE };
        } else {
          this.inventory.storage[i] = { itemId: id, count: MAX_STACK_SIZE };
        }
        this._render();
        return;
      }
    }
  }

  _createSlot(area, index) {
    // Minecraft slot style: dark inset border with lighter bottom/right
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px;
      height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
    `;

    slot.addEventListener('mouseenter', () => {
      slot.style.background = '#A0A0A0';
      const item = this.inventory.getItem(area, index);
      if (item && item.count > 0) {
        let name;
        if (item.itemId !== undefined) {
          name = ITEM_NAMES_MAP[item.itemId] || 'Objeto';
        } else if (item.toolId !== undefined) {
          name = item.toolType + ' (' + item.material + ')';
        } else {
          name = BLOCK_NAMES[item.blockId] || 'Unknown';
        }
        this.tooltip.textContent = `${name}`;
        this.tooltip.style.display = 'block';
      }
    });

    slot.addEventListener('mouseleave', () => {
      slot.style.background = '#8B8B8B';
      this.tooltip.style.display = 'none';
    });

    slot.addEventListener('click', (e) => {
      if (e.altKey) {
        this._onSlotAltClick(area, index, e.clientX, e.clientY);
        return;
      }
      if (e.shiftKey) {
        this._onShiftClick(area, index);
      } else {
        this._onSlotClick(area, index);
      }
    });

    slot.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (e.altKey) {
        this._onSlotAltClick(area, index, e.clientX, e.clientY);
        return;
      }
      this._onSlotRightClick(area, index);
    });

    return slot;
  }

  /**
   * Alt-click on a slot: show context menu with quick actions.
   */
  _onSlotAltClick(area, index, x, y) {
    const item = this.inventory.getItem(area, index);
    if (!item || item.count <= 0) return;
    this._showContextMenu(area, index, x, y);
  }

  _closeContextMenu() {
    if (this._contextMenu) {
      this._contextMenu.remove();
      this._contextMenu = null;
    }
    if (this._contextMenuCloseHandler) {
      document.removeEventListener('mousedown', this._contextMenuCloseHandler, true);
      this._contextMenuCloseHandler = null;
    }
  }

  _showContextMenu(area, index, x, y) {
    this._closeContextMenu();
    const item = this.inventory.getItem(area, index);
    if (!item) return;

    const menu = document.createElement('div');
    menu.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      z-index: 700;
      background: #C6C6C6;
      border: 2px solid;
      border-color: #FFFFFF #373737 #373737 #FFFFFF;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #404040;
      padding: 2px;
      min-width: 140px;
      box-shadow: 3px 3px 0 rgba(0,0,0,0.4);
      user-select: none;
    `;

    const moveLabel = area === 'hotbar' ? 'Mover a almacenamiento' : 'Mover a hotbar';
    const options = [
      { label: 'Soltar 1', action: () => this._dropOne(area, index) },
      { label: 'Soltar todo', action: () => this._dropAll(area, index) },
      { label: moveLabel, action: () => this._onShiftClick(area, index) },
    ];

    for (const opt of options) {
      const row = document.createElement('div');
      row.textContent = opt.label;
      row.style.cssText = `
        padding: 4px 8px;
        cursor: pointer;
        text-shadow: 1px 1px 0 rgba(255,255,255,0.4);
      `;
      row.addEventListener('mouseenter', () => {
        row.style.background = '#5000A0';
        row.style.color = '#FFFFFF';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = 'transparent';
        row.style.color = '#404040';
      });
      row.addEventListener('click', (ev) => {
        ev.stopPropagation();
        opt.action();
        this._closeContextMenu();
        this._render();
      });
      menu.appendChild(row);
    }

    document.body.appendChild(menu);
    this._contextMenu = menu;

    // Clamp to viewport
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menu.style.left = (window.innerWidth - rect.width - 4) + 'px';
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = (window.innerHeight - rect.height - 4) + 'px';
    }

    this._contextMenuCloseHandler = (ev) => {
      if (!menu.contains(ev.target)) {
        this._closeContextMenu();
      }
    };
    // Defer to avoid catching the originating click
    setTimeout(() => {
      if (this._contextMenuCloseHandler) {
        document.addEventListener('mousedown', this._contextMenuCloseHandler, true);
      }
    }, 0);
  }

  /** Drop a single item from a slot onto the cursor (held item). */
  _dropOne(area, index) {
    const item = this.inventory.getItem(area, index);
    if (!item || item.count <= 0) return;
    if (this.heldItem && !this._canStack(this.heldItem, item)) return;
    if (this.heldItem && this.heldItem.count >= MAX_STACK_SIZE) return;
    if (this.heldItem) {
      this.heldItem.count++;
    } else {
      this.heldItem = this._cloneOne(item);
      this.heldFrom = { area, index };
    }
    item.count--;
    if (item.count <= 0) this.inventory.setItem(area, index, null);
  }

  /** Drop the full stack onto the cursor (held item). */
  _dropAll(area, index) {
    const item = this.inventory.getItem(area, index);
    if (!item || item.count <= 0) return;
    if (this.heldItem) {
      if (!this._canStack(this.heldItem, item)) return;
      const space = MAX_STACK_SIZE - this.heldItem.count;
      const toMove = Math.min(space, item.count);
      this.heldItem.count += toMove;
      item.count -= toMove;
      if (item.count <= 0) this.inventory.setItem(area, index, null);
    } else {
      this.heldItem = { ...item };
      this.heldFrom = { area, index };
      this.inventory.setItem(area, index, null);
    }
  }

  /**
   * Shift-click: quick-move items between hotbar and storage.
   * Hotbar (slots 0-8) -> first empty storage slot (9-35)
   * Storage (slots 9-35) -> first empty hotbar slot (0-8)
   */
  _onShiftClick(area, index) {
    const item = this.inventory.getItem(area, index);
    if (!item || item.count <= 0) return;

    if (area === 'hotbar') {
      // Move from hotbar to first empty storage slot
      for (let i = 0; i < this.inventory.storage.length; i++) {
        if (!this.inventory.storage[i]) {
          this.inventory.storage[i] = { ...item };
          this.inventory.setItem(area, index, null);
          this._render();
          return;
        }
      }
    } else if (area === 'storage') {
      // Move from storage to first empty hotbar slot
      for (let i = 0; i < this.inventory.slots.length; i++) {
        if (!this.inventory.slots[i]) {
          this.inventory.slots[i] = { ...item };
          this.inventory.setItem(area, index, null);
          this._render();
          return;
        }
      }
    }
  }

  // Check if two items can stack (same type)
  _canStack(a, b) {
    if (!a || !b) return false;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    return false;
  }

  // Clone a single item with count 1
  _cloneOne(item) {
    if (item.itemId !== undefined) return { itemId: item.itemId, count: 1 };
    return { blockId: item.blockId, count: 1 };
  }

  _onSlotClick(area, index) {
    const clickedItem = this.inventory.getItem(area, index);

    if (this.heldItem) {
      if (!clickedItem || clickedItem.count === 0) {
        this.inventory.setItem(area, index, { ...this.heldItem });
        this.heldItem = null;
        this.heldFrom = null;
      } else if (this._canStack(clickedItem, this.heldItem)) {
        const space = MAX_STACK_SIZE - clickedItem.count;
        const toMove = Math.min(space, this.heldItem.count);
        clickedItem.count += toMove;
        this.heldItem.count -= toMove;
        if (this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      } else {
        this.inventory.setItem(area, index, { ...this.heldItem });
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
      }
    } else {
      if (clickedItem && clickedItem.count > 0) {
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
        this.inventory.setItem(area, index, null);
      }
    }

    this._render();
  }

  _onSlotRightClick(area, index) {
    const clickedItem = this.inventory.getItem(area, index);

    if (this.heldItem) {
      if (!clickedItem || clickedItem.count === 0) {
        this.inventory.setItem(area, index, this._cloneOne(this.heldItem));
        this.heldItem.count--;
        if (this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      } else if (this._canStack(clickedItem, this.heldItem) && clickedItem.count < MAX_STACK_SIZE) {
        clickedItem.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      }
    } else {
      if (clickedItem && clickedItem.count > 1) {
        const half = Math.ceil(clickedItem.count / 2);
        this.heldItem = { ...clickedItem, count: half };
        clickedItem.count -= half;
        this.heldFrom = { area, index };
      } else if (clickedItem && clickedItem.count === 1) {
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
        this.inventory.setItem(area, index, null);
      }
    }

    this._render();
  }

  /** Connect sound manager for UI sounds */
  setSoundManager(sm) {
    this._soundManager = sm;
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.heldItem = null;
    this.heldFrom = null;
    document.exitPointerLock();
    this.overlay.style.display = 'flex';
    document.addEventListener('mousemove', this._onMouseMove);
    this._render();
    if (this._soundManager) this._soundManager.playInventoryOpen();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this._soundManager) this._soundManager.playInventoryClose();
    this.overlay.style.display = 'none';
    this.cursorItem.style.display = 'none';
    this.tooltip.style.display = 'none';
    this._closeContextMenu();

    document.removeEventListener('mousemove', this._onMouseMove);

    if (this.heldItem && this.heldFrom) {
      const existing = this.inventory.getItem(this.heldFrom.area, this.heldFrom.index);
      if (!existing) {
        this.inventory.setItem(this.heldFrom.area, this.heldFrom.index, this.heldItem);
      } else {
        let added;
        if (this.heldItem.toolType || this.heldItem.toolId) {
          added = this.inventory.addTool(this.heldItem);
        } else if (this.heldItem.itemId !== undefined) {
          added = this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
        } else if (this.heldItem.blockId !== undefined) {
          added = true;
          for (let c = 0; c < (this.heldItem.count || 1); c++) {
            if (!this.inventory.addBlock(this.heldItem.blockId)) { added = false; break; }
          }
        }
        if (!added) {
          this.inventory.setItem(this.heldFrom.area, this.heldFrom.index, this.heldItem);
        }
      }
    } else if (this.heldItem) {
      if (this.heldItem.toolType || this.heldItem.toolId) {
        this.inventory.addTool(this.heldItem);
      } else if (this.heldItem.itemId !== undefined) {
        this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
      } else if (this.heldItem.blockId !== undefined) {
        for (let c = 0; c < (this.heldItem.count || 1); c++) {
          this.inventory.addBlock(this.heldItem.blockId);
        }
      }
    }
    this.heldItem = null;
    this.heldFrom = null;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  _render() {
    for (let i = 0; i < 27; i++) {
      this._renderSlot(this.storageSlots[i], this.inventory.storage[i]);
    }
    for (let i = 0; i < 9; i++) {
      const el = this.hotbarSlotEls[i];
      this._renderSlot(el, this.inventory.slots[i]);
      if (i === this.inventory.selectedIndex) {
        el.style.borderColor = '#FFFFFF #373737 #373737 #FFFFFF';
        el.style.background = '#A8A8A8';
      }
    }

    if (this.heldItem && this.heldItem.count > 0) {
      this.cursorItem.style.display = 'block';
      let heldIcon;
      if (this.heldItem.itemId !== undefined) {
        heldIcon = renderItemIcon(this.heldItem.itemId, 32);
      } else {
        heldIcon = renderBlockIcon(this.heldItem.blockId, 32);
      }
      this.cursorItem.innerHTML = `
        <div style="position:relative;">
          ${heldIcon}
          <span style="
            position:absolute;bottom:-2px;right:-2px;
            font-family:'Courier New',monospace;font-size:12px;font-weight:bold;
            color:white;text-shadow:2px 2px 0 #3F3F3F;
          ">${this.heldItem.count > 1 ? this.heldItem.count : ''}</span>
        </div>
      `;
    } else {
      this.cursorItem.style.display = 'none';
    }
  }

  _renderSlot(el, item) {
    if (item && item.count > 0) {
      let iconHtml;
      if (item.itemId !== undefined) {
        iconHtml = renderItemIcon(item.itemId, 28);
      } else {
        iconHtml = renderBlockIcon(item.blockId, 28);
      }
      el.innerHTML = `
        ${iconHtml}
        <span style="font-size:11px;font-weight:bold;position:absolute;bottom:1px;right:3px;color:white;
          text-shadow:2px 2px 0 #3F3F3F, -1px 0 0 #3F3F3F;
          font-family:'Courier New',monospace;">${item.count > 1 ? item.count : ''}</span>
      `;
    } else {
      el.innerHTML = '';
    }
  }
}
